import { KIND_LABEL, RELATION_LABEL, ROLE_AT } from "./Access";

// Configuration → Roles: what each role lets someone do, where it is given, and who gives it. A
// reference page, below the line in the sidebar (docs/DESIGN.md, "The left nav"): it changes
// nothing, and says where each role is changed. Its words are Access's words.

type Role = keyof typeof RELATION_LABEL;

export interface RoleRef {
  role: string;
  at: string;
  lets: string;
  givenBy: string;
}

const at = (r: Role) => ROLE_AT[r].map((k) => (k === "platform" ? "Preva" : KIND_LABEL[k].toLowerCase()))
  .join(", ").replace(/^./, (c) => c.toUpperCase());

/** Every role, in the order people meet them: working with student work first, then running
 *  organizations, then Preva's own. */
export const ROLES: RoleRef[] = [
  { role: RELATION_LABEL.viewer, at: at("viewer"),
    lets: "See student work there and everywhere below it: the papers, their scores and feedback.",
    givenBy: "Its configurators; for now, anyone who edits there." },
  { role: RELATION_LABEL.editor, at: at("editor"),
    lets: "See and change student work there and below: scoring, feedback, releasing, connecting a Drive.",
    givenBy: "Its configurators; for now, anyone who edits there." },
  { role: RELATION_LABEL.data_reader, at: at("data_reader"),
    lets: "Read its School Improvement data. Kept apart from student work: it shows no papers.",
    givenBy: "Its configurators." },
  { role: RELATION_LABEL.configurator, at: at("configurator"),
    lets: "Change its settings and give roles in it; a district's configurators configure its schools, "
      + "classes and cohorts too. Seeing student work is a role of its own.",
    givenBy: "Its configurators, and operators." },
  { role: RELATION_LABEL.chatter, at: at("chatter"),
    lets: "Use School Improvement's chat. Each answer is an AI call Preva pays for, so it is given, "
      + "never open to all. At a district it works only once an operator turns chat on there.",
    givenBy: "At a district, its configurators, to its own people; elsewhere, operators." },
  { role: RELATION_LABEL.curriculum_writer, at: at("curriculum_writer"),
    lets: "Change and publish the district's own version of a curriculum. Configuring the district "
      + "does not include it.",
    givenBy: "The district's configurators, to its own people." },
  { role: "Writes a curriculum", at: "One content provider's curriculum",
    lets: "Change that curriculum's draft in Content. Publishing is for the provider's configurators.",
    givenBy: "The provider's configurators, in Content → Curricula → Writers." },
  { role: RELATION_LABEL.operator, at: at("operator"),
    lets: "Run the platform: configure every district and content provider, and give any role. "
      + "Sees no student work and no provider's content by being one.",
    givenBy: "Operators." },
  { role: `${RELATION_LABEL.scoring_viewer}, ${RELATION_LABEL.scoring_maintainer.toLowerCase()}, `
      + RELATION_LABEL.scoring_lead.toLowerCase(), at: at("scoring_viewer"),
    lets: "Use Scoring. A viewer reads raters and scorecards; a maintainer also builds evaluation sets "
      + "and runs experiments, which call the model; a lead also records scorecards.",
    givenBy: "Operators." },
  { role: RELATION_LABEL.curriculum_reviewer, at: at("curriculum_reviewer"),
    lets: "View every content provider's curricula, drafts included, and change nothing. Works only "
      + "for an operator.",
    givenBy: "Operators." },
  { role: "Belongs to", at: "One organization: a district, a content provider, or Preva",
    lets: "Being one of its people. It gives no role by itself; a district's people may chat if it "
      + "is given to all of them.",
    givenBy: "On People: the organization's configurators, and operators." },
];

export function Roles() {
  return (
    <div className="gl pp">
      <h2>Roles</h2>
      <p className="gl-lede">Roles are given and ended on Access.</p>
      <table className="tbl">
        <thead><tr><th>Role</th><th>Given at</th><th>Lets them</th><th>Who gives it</th></tr></thead>
        <tbody>
          {ROLES.map((r) => (
            <tr key={r.role}>
              <td>{r.role}</td>
              <td>{r.at}</td>
              <td>{r.lets}</td>
              <td>{r.givenBy}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
